import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  LifeBuoy, Sparkles, Send, BookOpen, Ticket, Loader2, Plus, MessageSquare,
  CheckCircle2, ArrowLeft, Search, ChevronRight, Mail, ShieldQuestion,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUS_COLORS = {
  open: "bg-blue-500/15 text-blue-400",
  awaiting_user: "bg-amber-500/15 text-amber-400",
  resolved: "bg-emerald-500/15 text-emerald-400",
  closed: "bg-slate-500/15 text-slate-400",
};
const STARTERS = [
  "How do AI credits work?",
  "How do I publish my game?",
  "Can I make a 3D model?",
  "How do I edit a character?",
];

export default function Support() {
  return (
    <div className="p-5 lg:p-8 max-w-5xl mx-auto" data-testid="support-page">
      <PageHeader title="Support Centre" icon={LifeBuoy} subtitle="Get instant answers from our AI assistant, browse the knowledge base, or open a ticket." />
      <Tabs defaultValue="assistant">
        <TabsList>
          <TabsTrigger value="assistant" data-testid="support-tab-assistant"><Sparkles className="w-4 h-4 mr-1.5" /> Help Assistant</TabsTrigger>
          <TabsTrigger value="kb" data-testid="support-tab-kb"><BookOpen className="w-4 h-4 mr-1.5" /> Knowledge base</TabsTrigger>
          <TabsTrigger value="tickets" data-testid="support-tab-tickets"><Ticket className="w-4 h-4 mr-1.5" /> My tickets</TabsTrigger>
        </TabsList>
        <TabsContent value="assistant" className="mt-4"><Assistant /></TabsContent>
        <TabsContent value="kb" className="mt-4"><KnowledgeBase /></TabsContent>
        <TabsContent value="tickets" className="mt-4"><Tickets /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- AI Assistant ---------------- */
function Assistant() {
  const [msgs, setMsgs] = useState([
    { role: "assistant", content: "Hi! I'm the GameView Support Assistant. Ask me anything about building, publishing, credits, characters or your account." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sid, setSid] = useState(null);
  const [articles, setArticles] = useState([]);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, sending]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || sending) return;
    setInput("");
    const history = msgs.filter((m) => m.role !== "system").map((m) => ({ role: m.role, content: m.content }));
    setMsgs((m) => [...m, { role: "user", content: message }]);
    setSending(true);
    try {
      const { data } = await api.post("/support/assistant", { message, session_id: sid, history });
      setSid(data.session_id);
      setMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
      setArticles(data.articles || []);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", content: "Sorry, I hit a snag. Try again, or open a ticket from the My tickets tab." }]);
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] flex flex-col" style={{ minHeight: 460 }}>
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="assistant-messages" style={{ maxHeight: 460 }}>
          {msgs.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[hsl(var(--primary))] text-white rounded-br-sm" : "bg-[hsl(var(--secondary))] rounded-bl-sm"}`}
                data-testid={m.role === "assistant" ? "assistant-reply" : "assistant-user-msg"}>
                {m.content}
              </div>
            </div>
          ))}
          {sending && <div className="flex justify-start"><div className="bg-[hsl(var(--secondary))] rounded-2xl rounded-bl-sm px-4 py-2.5"><Loader2 className="w-4 h-4 animate-spin" /></div></div>}
          <div ref={endRef} />
        </div>
        {msgs.length <= 1 && (
          <div className="flex flex-wrap gap-2 px-4 pb-2">
            {STARTERS.map((s) => (
              <button key={s} onClick={() => send(s)} data-testid={`assistant-starter-${s.slice(0, 8)}`}
                className="text-xs px-3 py-1.5 rounded-full bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--primary))]/20 transition-colors">{s}</button>
            ))}
          </div>
        )}
        <div className="border-t border-[hsl(var(--border))] p-3 flex gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Ask the assistant…" data-testid="assistant-input" />
          <Button onClick={() => send()} disabled={sending || !input.trim()} data-testid="assistant-send"
            className="rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff] shrink-0">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
        <div className="font-bold text-sm mb-2 flex items-center gap-1.5"><BookOpen className="w-4 h-4 text-[hsl(var(--primary))]" /> Related help</div>
        {articles.length === 0 ? <p className="text-xs text-[hsl(var(--muted-foreground))]">Ask a question and I'll point you to the right guides.</p> :
          <div className="space-y-2" data-testid="assistant-related">
            {articles.map((a) => (
              <div key={a.id} className="rounded-xl border border-[hsl(var(--border))] p-3">
                <div className="font-semibold text-sm">{a.title}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] line-clamp-2">{a.summary}</div>
              </div>
            ))}
          </div>}
      </div>
    </div>
  );
}

/* ---------------- Knowledge base ---------------- */
function KnowledgeBase() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    api.get("/support/kb", { params: { q, category: cat } }).then((r) => setData(r.data));
  }, [q, cat]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  if (!data) return <Spinner />;
  return (
    <div data-testid="kb-list">
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search help articles…" className="pl-9" data-testid="kb-search" />
        </div>
        <Select value={cat || "all"} onValueChange={(v) => setCat(v === "all" ? "" : v)}>
          <SelectTrigger className="w-56" data-testid="kb-category"><SelectValue placeholder="All categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {data.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {data.articles.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[hsl(var(--border))] p-10 text-center text-[hsl(var(--muted-foreground))]" data-testid="kb-empty">
          No articles matched. Try the Help Assistant or open a ticket.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {data.articles.map((a) => (
            <button key={a.id} onClick={() => setOpen(a.id)} data-testid={`kb-article-${a.id}`}
              className="text-left rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 hover:border-[hsl(var(--primary))]/60 hover:-translate-y-0.5 transition-all">
              <Badge variant="secondary" className="text-[10px] mb-1.5">{a.category}</Badge>
              <div className="font-bold flex items-center justify-between gap-2">{a.title} <ChevronRight className="w-4 h-4 opacity-50 shrink-0" /></div>
              <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5 line-clamp-2">{a.summary}</div>
            </button>
          ))}
        </div>
      )}
      <ArticleDialog id={open} onClose={() => setOpen(null)} />
    </div>
  );
}

function ArticleDialog({ id, onClose }) {
  const [a, setA] = useState(null);
  useEffect(() => {
    if (!id) { setA(null); return; }
    api.get(`/support/kb/${id}`).then((r) => setA(r.data)).catch(() => {});
  }, [id]);
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="kb-article-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BookOpen className="w-5 h-5 text-[hsl(var(--primary))]" /> {a?.title || "Loading…"}</DialogTitle>
          <DialogDescription>{a?.category}</DialogDescription>
        </DialogHeader>
        {a && <div className="text-sm whitespace-pre-wrap leading-relaxed text-[hsl(var(--foreground))]">{a.body}</div>}
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Tickets ---------------- */
function Tickets() {
  const { user } = useAuth();
  const isAdmin = ["admin", "superadmin"].includes(user?.role);
  const [data, setData] = useState(null);
  const [scope, setScope] = useState("mine");
  const [creating, setCreating] = useState(false);
  const [active, setActive] = useState(null);

  const load = useCallback(() => {
    api.get("/support/tickets", { params: { all: isAdmin && scope === "all" } }).then((r) => setData(r.data));
  }, [isAdmin, scope]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <Spinner />;
  return (
    <div data-testid="tickets-list">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="w-44" data-testid="tickets-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="mine">My tickets</SelectItem>
                <SelectItem value="all">All tickets (admin)</SelectItem>
              </SelectContent>
            </Select>
          )}
          <span className="text-sm text-[hsl(var(--muted-foreground))]">{data.tickets.length} ticket{data.tickets.length === 1 ? "" : "s"}</span>
        </div>
        <Button onClick={() => setCreating(true)} data-testid="new-ticket-btn" className="rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]">
          <Plus className="w-4 h-4" /> New ticket
        </Button>
      </div>

      {data.tickets.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[hsl(var(--border))] p-10 text-center text-[hsl(var(--muted-foreground))]" data-testid="tickets-empty">
          <ShieldQuestion className="w-8 h-8 mx-auto mb-2 opacity-50" />
          No tickets yet. Open one and our team (and AI) will help you out.
        </div>
      ) : (
        <div className="space-y-2">
          {data.tickets.map((t) => (
            <button key={t.id} onClick={() => setActive(t.id)} data-testid={`ticket-row-${t.id}`}
              className="w-full text-left rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 hover:border-[hsl(var(--primary))]/60 transition-all flex items-center gap-3">
              <MessageSquare className="w-5 h-5 text-[hsl(var(--muted-foreground))] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate">{t.subject} <span className="text-xs text-[hsl(var(--muted-foreground))]">#{t.ref}</span></div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{isAdmin && scope === "all" ? `${t.user_name} · ` : ""}{t.category} · {t.messages?.length || 0} messages</div>
              </div>
              <Badge className={`capitalize text-[10px] ${STATUS_COLORS[t.status] || ""}`}>{(t.status || "").replace("_", " ")}</Badge>
            </button>
          ))}
        </div>
      )}

      <NewTicketDialog open={creating} categories={data.categories} onClose={() => setCreating(false)} onCreated={(t) => { load(); setActive(t.id); }} />
      <TicketDialog id={active} isAdmin={isAdmin} onClose={() => setActive(null)} onChange={load} />
    </div>
  );
}

function NewTicketDialog({ open, categories, onClose, onCreated }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("general");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setSubject(""); setMessage(""); setCategory("general"); } }, [open]);

  const submit = async () => {
    if (subject.trim().length < 3 || message.trim().length < 3) { toast.error("Add a subject and a short description."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/support/tickets", { subject, message, category });
      toast.success(`Ticket #${data.ref} created — we'll be in touch!`);
      onClose(); onCreated(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-ticket-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Ticket className="w-5 h-5 text-[hsl(var(--primary))]" /> Open a support ticket</DialogTitle>
          <DialogDescription>Tell us what's up and we'll get back to you. You'll also get an email update.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" data-testid="ticket-subject" />
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="ticket-category"><SelectValue /></SelectTrigger>
            <SelectContent>{(categories || []).map((c) => <SelectItem key={c} value={c} className="capitalize">{c.replace("_", " ")}</SelectItem>)}</SelectContent>
          </Select>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5} placeholder="Describe your issue or question…" data-testid="ticket-message" />
          <Button onClick={submit} disabled={saving} className="w-full rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" data-testid="ticket-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit ticket
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TicketDialog({ id, isAdmin, onClose, onChange }) {
  const [t, setT] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(() => {
    if (!id) { setT(null); return; }
    api.get(`/support/tickets/${id}`).then((r) => setT(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [t]);

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/support/tickets/${id}/reply`, { message: reply });
      setT(data); setReply(""); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  const close = async () => {
    try { await api.post(`/support/tickets/${id}/close`); toast.success("Ticket closed"); onChange(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="ticket-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><MessageSquare className="w-5 h-5 text-[hsl(var(--primary))]" /> {t?.subject || "Ticket"}</DialogTitle>
          <DialogDescription>{t ? `#${t.ref} · ${t.category}${isAdmin ? ` · ${t.user_name} (${t.user_email})` : ""}` : "Loading…"}</DialogDescription>
        </DialogHeader>
        {t && (
          <>
            <div className="-mt-1">
              <span className={`inline-block capitalize text-[10px] font-semibold px-2.5 py-1 rounded-full ${STATUS_COLORS[t.status] || ""}`} data-testid="ticket-status">{(t.status || "").replace("_", " ")}</span>
            </div>
            <div className="space-y-3 max-h-[40vh] overflow-y-auto pr-1 mt-1" data-testid="ticket-thread">
              {(t.messages || []).map((m) => (
                <div key={m.id} className={`flex ${m.role === "support" ? "justify-start" : "justify-end"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap ${m.role === "support" ? "bg-[hsl(var(--primary))]/15 border border-[hsl(var(--primary))]/30 rounded-bl-sm" : "bg-[hsl(var(--secondary))] rounded-br-sm"}`}>
                    <div className="text-[10px] font-bold opacity-70 mb-0.5">{m.role === "support" ? "Support" : m.author}</div>
                    {m.content}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
            {t.status !== "closed" ? (
              <div className="border-t border-[hsl(var(--border))] pt-3 space-y-2">
                <Textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={2}
                  placeholder={isAdmin ? "Reply to the user…" : "Add a reply…"} data-testid="ticket-reply-input" />
                <div className="flex gap-2">
                  <Button onClick={send} disabled={sending || !reply.trim()} className="rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" data-testid="ticket-reply-send">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send reply
                  </Button>
                  <Button onClick={close} variant="outline" className="rounded-full" data-testid="ticket-close-btn"><CheckCircle2 className="w-4 h-4" /> Close ticket</Button>
                </div>
              </div>
            ) : (
              <div className="border-t border-[hsl(var(--border))] pt-3 text-sm text-[hsl(var(--muted-foreground))] flex items-center gap-2" data-testid="ticket-closed-note">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" /> This ticket is closed.
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
